<template>
  <input
    class="range-slider"
    :max="maxValue"
    :min="minValue"
    ref="rangeInput"
    :step="step"
    type="range"
    v-model="modelValue"
    @input="updateValue"
    :aria-label="$t('general.labels.range')"
  />
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';

const props = defineProps({
  modelValue: { type: [Number, String], required: false, default: '' },
  maxValue: { type: [Number, String], required: false, default: '' },
  minValue: { type: [Number, String], required: false, default: '' },
  step: { type: [Number, String], required: false, default: 1 },
});

const emit = defineEmits(['update:modelValue']);

const rangeInput = ref();

onMounted(() => setRangeTrack());
watch(
  () => props.modelValue,
  () => setRangeTrack()
);

function updateValue() {
  emit('update:modelValue', props.modelValue);
  setRangeTrack();
}

const sliderColours = {
  primary: '#E89C00',
  secondary: '#282728',
};

/**
 * This calculates and sets a style on the thumb track.
 * ie. How much of the bar is orange in relation to the thumb on the track
 */
function setRangeTrack() {
  let value = ((props.modelValue - props.minValue) / (props.maxValue - props.minValue)) * 99;
  rangeInput.value.style.background = `linear-gradient(to right, ${sliderColours.primary} ${value}%, ${sliderColours.secondary} ${value}%`;
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

$thumb-size: rem(19);

@mixin appearance-none {
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
}

@mixin thumb-style {
  background: map-get($background-color, lightest);
  border-radius: 100%;
  border: none;
  cursor: pointer;
  height: $thumb-size;
  outline: none;
  width: $thumb-size;

  &:hover {
    background: map-get($yellow-color, default);
  }
}

.range-slider {
  &[type='range'] {
    @include appearance-none;
    border-radius: map-get($border-radius, heavier);
    height: 2px;
    width: 100%;

    &::-webkit-slider-thumb {
      @include appearance-none;
      @include thumb-style;
    }

    &::-moz-range-thumb {
      @include appearance-none;
      @include thumb-style;
    }
  }
}
</style>
